import React, { useState, useEffect } from 'react';
import { X, Building2, Loader2 } from 'lucide-react';
import { departmentsApi } from '../../lib/api/departmentsApi';

const DepartmentDialog = ({ open, onClose, onSuccess, department }) => {
  const isEdit = !!department?._id;
  const [name, setName] = useState('');
  const [status, setStatus] = useState('active');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(department?.name || '');
    setStatus(department?.status || 'active');
    setError('');
  }, [open, department]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmedName = (name || '').trim();
    if (!trimmedName) {
      setError('Team name is required.');
      return;
    }
    setSubmitLoading(true);
    try {
      let result = null;
      const payload = {
        name: trimmedName,
        status,
      };
      if (isEdit) {
        result = await departmentsApi.update(department._id, payload);
      } else {
        result = await departmentsApi.create(payload);
      }
      onSuccess?.(result);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Request failed');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" />
      <div
        className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-xl max-h-[90vh] flex flex-col"
        role="dialog"
        aria-labelledby="department-dialog-title"
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 shrink-0 bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
              <Building2 className="w-5 h-5" />
            </div>
            <h2 id="department-dialog-title" className="text-lg font-semibold text-gray-900">
              {isEdit ? 'Edit Team' : 'New Team'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 px-6 py-5">
          {error && (
            <div className="mb-4 rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700 shrink-0">
              {error}
            </div>
          )}

          {/* Basic info — name + status on one row when edit */}
          <div className="mb-5 shrink-0">
            <div
              className={`grid gap-4 ${isEdit ? 'grid-cols-1 sm:grid-cols-[1fr,140px]' : 'grid-cols-1'}`}
            >
              <div>
                <label
                  htmlFor="dept-name"
                  className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500"
                >
                  Team name
                </label>
                <input
                  id="dept-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Engineering, HR, Sales"
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition"
                  maxLength={100}
                  autoFocus
                />
              </div>
              {isEdit && (
                <div>
                  <label
                    htmlFor="dept-status"
                    className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500"
                  >
                    Status
                  </label>
                  <select
                    id="dept-status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm text-gray-900 bg-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Permissions selection omitted for now – all team users rely on user-level permissions */}

          <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50/50 pt-4 mt-4 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="save-team-btn"
              disabled={submitLoading}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-70 transition"
            >
              {submitLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Save changes' : 'Create team'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DepartmentDialog;
